import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { request, type ApiError } from '../lib/api.js';
import { apiPath } from '../lib/tenant.js';
import { fileKind, formatBytes, useDocumentViewer, type FileKind, type ViewerFile } from './DocumentViewer.js';
import { formatSpanExact, groupIntoPeriods, type Locale } from '@monrdv/shared';

/**
 * Files, wherever somebody meets them.
 *
 * One component, used by the job's attachments tab, by an invoice, by a quote, by the
 * customer page and by a conversation, because "open the attachment" has to behave the same
 * in all five. The alternative — a gallery on the job, a link list on the invoice, an anchor
 * in the inbox — is what the previous system did, and it is why nobody trusted that clicking
 * a file would show them the file.
 *
 * Two shapes, one behaviour:
 *
 *   - `AttachmentGrid`, for a page with room: thumbnails for photographs, a labelled tile
 *     for everything else.
 *   - `AttachmentChips`, for a line of a conversation: small, inline, and never taller than
 *     the message it belongs to.
 *
 * Both open the same viewer. Each instance owns its own, which costs nothing — the dialog
 * renders nothing until a file is chosen — and means a screen never has to thread state
 * through to a list it merely renders.
 */

/** What the API answers with. `downloadUrl` is optional so a chat attachment can share this. */
export type Attachment = {
  id: string;
  fileName: string;
  contentType: string | null;
  byteSize?: number | null;
  caption?: string | null;
  url?: string | null;
  downloadUrl?: string | null;
  width?: number | null;
  height?: number | null;
  createdAt?: string | null;
  uploadedByName?: string | null;
};

/**
 * Strings this component needs that the shared catalogue does not carry yet.
 *
 * Routed through i18next with a default rather than written into the markup, so the moment a
 * `documents` block lands in `@monrdv/shared` these disappear on their own and nothing here
 * has to change. They are listed in the handover note.
 */
/*
 * These strings lived here as inline fallbacks while another change owned the catalogue file.
 * They are real catalogue entries now, so the shim is gone: a fallback that works is a fallback
 * nobody removes, and the copy quietly stops being translatable.
 */
function useLabels() {
  const { t, i18n } = useTranslation();
  const language = i18n.language.startsWith('en') ? 'en' : 'fr';
  return { t, language, label: (key: string) => t(key) };
}

/**
 * The URL for one attachment's bytes.
 *
 * The attachments endpoints answer with a complete, slug-carrying `url`, and that is what is
 * used. The conversation endpoint does not yet — it returns the file's metadata without a
 * link — so rather than hide a customer's photograph until that route is extended, the URL
 * is composed through `apiPath`, the one function in this application that knows how a
 * business slug is attached to a path. Composing it here by hand would be the mistake;
 * asking the same helper every other request already asks is not.
 */
function bytesUrl(file: Attachment): string {
  return file.url ?? apiPath(`/media/${file.id}`);
}

function downloadUrlFor(file: Attachment): string {
  if (file.downloadUrl) return file.downloadUrl;
  const base = bytesUrl(file);
  return `${base}${base.includes('?') ? '&' : '?'}download=1`;
}

function toViewerFile(file: Attachment): ViewerFile {
  return {
    id: file.id,
    fileName: file.fileName,
    contentType: file.contentType,
    url: bytesUrl(file),
    downloadUrl: downloadUrlFor(file),
    caption: file.caption ?? null,
    byteSize: file.byteSize ?? null,
  };
}

/** The three or four letters that tell a PDF from a spreadsheet at a glance. */
function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  const extension = dot > 0 ? fileName.slice(dot + 1) : '';
  return extension.slice(0, 4).toUpperCase() || '—';
}

// ------------------------------------------------------------------------ grid ----

export function AttachmentGrid({
  items,
  emptyMessage,
}: {
  items: Attachment[];
  /** Overrides the generic "no files", where a screen has a better sentence. */
  emptyMessage?: string;
}) {
  const { label, language } = useLabels();
  const viewer = useDocumentViewer();

  if (items.length === 0) {
    return <p className="section__empty">{emptyMessage ?? label('documents.empty')}</p>;
  }

  /*
   * Grouped by when the files arrived.
   *
   * A customer's drawer runs to hundreds of files across several years, and an undivided grid
   * of thumbnails is the least searchable thing on the screen — every photo of a duct looks
   * like every other photo of a duct. The date is the only handle anybody actually has, and it
   * is usually remembered as a season rather than a day: *the ones from the spring we replaced
   * the unit*.
   *
   * Headings appear only when there is more than one period. On a job whose photographs were
   * all taken on one visit a single heading would divide nothing and only add a line to read.
   */
  const periods = groupIntoPeriods(items, (file) => file.createdAt, language as Locale);
  const undated = items.filter((file) => !file.createdAt);
  const divided = periods.length > 1 || (periods.length > 0 && undated.length > 0);

  if (!divided) return <Grid files={items} viewer={viewer} label={label} language={language} />;

  return (
    <>
      {periods.map((period) => (
        <div key={period.start.toISOString()} className="filegrid__period">
          <h4 className="period__head" title={formatSpanExact(period.start, period.end, language as Locale)}>
            <span className="period__label">{period.label}</span>
            <span className="period__rule" aria-hidden="true" />
            <span className="period__count">{period.items.length}</span>
          </h4>
          <Grid files={period.items} viewer={viewer} label={label} language={language} chrome={false} />
        </div>
      ))}

      {/* Files whose date never made it across. Shown last and named, rather than dropped. */}
      {undated.length > 0 && (
        <div className="filegrid__period">
          <h4 className="period__head">
            <span className="period__label">{label('documents.undated')}</span>
            <span className="period__rule" aria-hidden="true" />
            <span className="period__count">{undated.length}</span>
          </h4>
          <Grid files={undated} viewer={viewer} label={label} language={language} chrome={false} />
        </div>
      )}

      {viewer.element}
    </>
  );
}

/** The tiles themselves. `chrome` carries the viewer's dialog, which must be rendered once. */
function Grid({
  files,
  viewer,
  label,
  language,
  chrome = true,
}: {
  files: Attachment[];
  viewer: ReturnType<typeof useDocumentViewer>;
  label: (key: string) => string;
  language: string;
  chrome?: boolean;
}) {
  void label;
  return (
    <>
      <ul className="filegrid">
        {files.map((file) => {
          const kind = fileKind(file);
          const size = formatBytes(file.byteSize, language);
          return (
            <li key={file.id} className="filegrid__cell">
              <button
                type="button"
                className={`filetile filetile--${kind}`}
                onClick={() => viewer.open(toViewerFile(file))}
                title={file.fileName}
              >
                {kind === 'image' ? (
                  <img
                    className="filetile__thumb"
                    src={bytesUrl(file)}
                    alt={file.caption ?? file.fileName}
                    // The grid is long — a job has a median of seventeen photographs and some
                    // have hundreds — so the browser fetches what is on screen and no more.
                    loading="lazy"
                    decoding="async"
                    width={file.width ?? undefined}
                    height={file.height ?? undefined}
                  />
                ) : (
                  <span className="filetile__badge" aria-hidden="true">
                    {extensionOf(file.fileName)}
                  </span>
                )}
                <span className="filetile__name">{file.caption || file.fileName}</span>
                {size && <span className="filetile__size tnum">{size}</span>}
              </button>
            </li>
          );
        })}
      </ul>
      {chrome && viewer.element}
    </>
  );
}

// ----------------------------------------------------------------------- chips ----

export function AttachmentChips({ items }: { items: Attachment[] }) {
  const { label, language } = useLabels();
  const viewer = useDocumentViewer();
  if (items.length === 0) return null;

  return (
    <>
      <ul className="filechips">
        {items.map((file) => {
          const kind: FileKind = fileKind(file);
          /*
           * A row whose bytes were never imported is named, not linked. A link that 404s
           * hides which of the two cases you are looking at — the file is missing from the
           * export, or the viewer failed — and those need different people to fix them.
           */
          if (file.url === null) {
            return (
              <li key={file.id}>
                <span className="filechip filechip--absent" title={label('documents.missing')}>
                  <span className="filechip__badge">{extensionOf(file.fileName)}</span>
                  <span className="filechip__name">{file.fileName}</span>
                </span>
              </li>
            );
          }

          return (
            <li key={file.id}>
              <button
                type="button"
                className={`filechip filechip--${kind}`}
                onClick={() => viewer.open(toViewerFile(file))}
                title={file.fileName}
              >
                {kind === 'image' ? (
                  <img className="filechip__thumb" src={bytesUrl(file)} alt="" loading="lazy" decoding="async" />
                ) : (
                  <span className="filechip__badge" aria-hidden="true">
                    {extensionOf(file.fileName)}
                  </span>
                )}
                <span className="filechip__name">{file.caption || file.fileName}</span>
                {file.byteSize ? (
                  <span className="filechip__size tnum">{formatBytes(file.byteSize, language)}</span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
      {viewer.element}
    </>
  );
}

// --------------------------------------------------------------------- section ----

type Scope =
  | { jobId: string }
  | { customerId: string }
  | { invoiceId: string }
  | { quoteId: string };

/**
 * A titled band of files, fetched for whatever record the screen is showing.
 *
 * Its own query rather than a field on the record, for the same reason the customer page
 * splits its sections: a file appearing must not force an invoice's totals to refetch, and
 * an invoice with no attachments must not cost a second query on every other screen.
 *
 * It renders nothing at all when there are no files and the screen did not ask for the empty
 * state. An invoice page that grew a permanently empty "Files" heading would be worse than
 * the page it replaced.
 */
export function AttachmentSection({
  scope,
  title,
  showWhenEmpty = false,
}: {
  scope: Scope;
  title?: string;
  showWhenEmpty?: boolean;
}) {
  const { t, label } = useLabels();
  const [key, value] = Object.entries(scope)[0] as [string, string];

  const query = useQuery({
    // Prefixed with the record so the realtime layer's invalidation for that record reaches
    // its files too.
    queryKey: [key.replace(/Id$/, ''), value, 'attachments'],
    queryFn: ({ signal }) =>
      request<{ items: Attachment[] }>(`/attachments?${key}=${value}&limit=200`, { signal }),
  });

  const items = query.data?.items ?? [];

  if (query.isPending) {
    // Nothing at all rather than a heading that appears and then withdraws: most invoices
    // have no files, and a band that flashes into existence and vanishes reads as a fault.
    if (!showWhenEmpty) return null;
    return (
      <section className="section">
        <header className="section__head">
          <h2 className="section__title">{title ?? label('documents.title')}</h2>
        </header>
        <div className="section__body">
          <p className="section__empty">{t('common.loading')}</p>
        </div>
      </section>
    );
  }

  /*
   * A 404 here does not mean "this record has nothing". A missing record answers with an
   * empty list, by construction — the scope is a subquery, and a scope that matches nothing
   * matches no files. A 404 means the endpoint itself is absent, which is true of the
   * development mock and of any deployment running an older API. Showing an alarming red box
   * on three screens for that would be crying wolf, so it renders nothing.
   */
  if (query.isError && (query.error as ApiError | null)?.status === 404) return null;

  // Everything else is a real failure. A screen that shows "no files" when the request fell
  // over is telling the reader something untrue.
  if (query.isError) {
    return (
      <section className="section">
        <header className="section__head">
          <h2 className="section__title">{title ?? label('documents.title')}</h2>
        </header>
        <div className="section__body">
          <p className="notice notice--error" role="alert">
            {t('errors.generic')}
          </p>
        </div>
      </section>
    );
  }

  if (items.length === 0 && !showWhenEmpty) return null;

  return (
    <section className="section">
      <header className="section__head">
        <h2 className="section__title">{title ?? label('documents.title')}</h2>
        <span className="section__count tnum">{items.length}</span>
      </header>
      <div className="section__body">
        <AttachmentGrid items={items} />
      </div>
    </section>
  );
}
